// @tennis-matches/services/api/api-auth-headers
// Bearer für Express-API (/api/*) inkl. Session-Refresh.

import {
  getSupabaseClient,
  hydrateSupabaseAuthBearerCache,
  readSupabaseClientConfig,
  syncSupabaseAuthBearerFromSession,
} from "../supabase/index.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

/**
 * @param {{ forceRefresh?: boolean }} [opts]
 * @returns {Promise<{ ok: true, headers: Record<string, string> } | { ok: false, reason: string }>}
 */
export async function resolveApiAuthHeaders(opts = {}) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const forceRefresh = opts.forceRefresh === true;

  await hydrateSupabaseAuthBearerCache();

  if (forceRefresh) {
    const refreshed = await supabase.auth.refreshSession();
    if (refreshed?.data?.session) {
      syncSupabaseAuthBearerFromSession(refreshed.data.session);
    }
  }

  let userCheck = await supabase.auth.getUser();
  if (userCheck.error || !userCheck.data?.user) {
    const refreshed = await supabase.auth.refreshSession();
    if (refreshed?.data?.session) {
      syncSupabaseAuthBearerFromSession(refreshed.data.session);
    }
    userCheck = await supabase.auth.getUser();
  }

  if (userCheck.error || !userCheck.data?.user) {
    syncSupabaseAuthBearerFromSession(null);
    return {
      ok: false,
      reason: userCheck.error ? "invalid-token" : "no-auth",
    };
  }

  const { data: sessionWrap } = await supabase.auth.getSession();
  const session = sessionWrap?.session || null;
  const tok = __asStr(session?.access_token);
  if (!tok) {
    syncSupabaseAuthBearerFromSession(null);
    return { ok: false, reason: "no-auth" };
  }

  syncSupabaseAuthBearerFromSession(session);

  const { anonKey } = readSupabaseClientConfig();
  const headers = { Authorization: `Bearer ${tok}` };
  if (anonKey) headers.apikey = anonKey;

  return { ok: true, headers };
}

/**
 * Admin-API: Cookie-Session (credentials) + optional Supabase-Bearer für Superadmin per E-Mail-Login.
 * @param {Record<string, string>} [extra]
 * @returns {Promise<Record<string, string>>}
 */
export async function resolveAdminApiHeaders(extra = {}) {
  const headers = { "Content-Type": "application/json", ...extra };
  try {
    const auth = await resolveApiAuthHeaders();
    if (auth.ok && auth.headers?.Authorization) {
      headers.Authorization = auth.headers.Authorization;
      if (auth.headers.apikey) headers.apikey = auth.headers.apikey;
    }
  } catch (_) {}
  return headers;
}
